import { z } from '@hono/zod-openapi'

export const OneRosterAcademicSessionSchema = z.object({
  sourcedId: z.string().openapi({ example: 'ses-2025-2026' }),
  title: z.string().openapi({ example: '2025-2026 School Year' }),
  type: z.enum(['gradingPeriod', 'semester', 'schoolYear', 'term']),
  startDate: z.string().date(),
  endDate: z.string().date(),
  schoolYear: z.string().openapi({ example: '2025-2026' }),
  status: z.enum(['active', 'inactive', 'tobe-deleted']).nullable(),
  metadata: z.record(z.unknown()).nullable(),
}).openapi('OneRosterAcademicSession')

export const OneRosterOrgSchema = z.object({
  sourcedId: z.string().openapi({ example: 'org-wa-12345' }),
  name: z.string().openapi({ example: 'Washington School District' }),
  type: z.enum(['district', 'school', 'building', 'department']),
  identifier: z.string().nullable(),
  parent: z.object({ sourcedId: z.string() }).nullable(),
  status: z.enum(['active', 'inactive', 'tobe-deleted']).nullable(),
  metadata: z.record(z.unknown()).nullable(),
}).openapi('OneRosterOrg')

export const OneRosterUserSchema = z.object({
  sourcedId: z.string().openapi({ example: 'user-teacher-01' }),
  username: z.string().nullable(),
  givenName: z.string(),
  familyName: z.string(),
  middleName: z.string().nullable(),
  email: z.string().email().nullable(),
  phone: z.string().nullable(),
  sms: z.string().nullable(),
  userIds: z.array(z.object({
    type: z.string().openapi({ example: 'staffId' }),
    identifier: z.string(),
  })).nullable(),
  enabledUser: z.boolean(),
  role: z.enum(['administrator', 'aide', 'guardian', 'parent', 'proctor',
    'student', 'teacher', 'observer', 'specialist', 'districtAdmin',
    'principal', 'counselor', 'staff',
  ]),
  grades: z.array(z.string()).nullable(),
  orgs: z.array(z.object({ sourcedId: z.string() })).nullable(),
  status: z.enum(['active', 'inactive', 'tobe-deleted']).nullable(),
  metadata: z.record(z.unknown()).nullable(),
}).openapi('OneRosterUser')

export const OneRosterEnrollmentSchema = z.object({
  sourcedId: z.string(),
  class: z.object({ sourcedId: z.string() }),
  school: z.object({ sourcedId: z.string() }).nullable(),
  user: z.object({ sourcedId: z.string() }),
  role: z.enum(['student', 'teacher', 'ta', 'observer', 'proctor', 'specialist', 'substitute']),
  status: z.enum(['active', 'inactive', 'tobe-deleted']).nullable(),
  metadata: z.record(z.unknown()).nullable(),
}).openapi('OneRosterEnrollment')

export const OneRosterClassSchema = z.object({
  sourcedId: z.string(),
  title: z.string().openapi({ example: 'Math 5 - Section A' }),
  course: z.object({ sourcedId: z.string() }),
  school: z.object({ sourcedId: z.string() }),
  term: z.object({ sourcedId: z.string() }),
  grades: z.array(z.string()).nullable(),
  subjectCodes: z.array(z.string()).nullable(),
  status: z.enum(['active', 'inactive', 'tobe-deleted']).nullable(),
  metadata: z.record(z.unknown()).nullable(),
}).openapi('OneRosterClass')

export const CleverStudentSchema = z.object({
  id: z.string().openapi({ example: '6123456789abcdef' }),
  name: z.object({ first: z.string(), middle: z.string().nullable(), last: z.string() }),
  email: z.string().email().nullable(),
  district: z.string(),
  school: z.string(),
  grade: z.string().nullable().openapi({ example: '5' }),
  dob: z.string().date().nullable(),
  ell_status: z.string().nullable(),
  frl_status: z.string().nullable(),
  iep_status: z.string().nullable(),
  race: z.string().nullable(),
  gender: z.string().nullable(),
  student_number: z.string().nullable(),
  state_id: z.string().nullable(),
}).openapi('CleverStudent')

export const CleverTeacherSchema = z.object({
  id: z.string(),
  name: z.object({ first: z.string(), middle: z.string().nullable(), last: z.string() }),
  email: z.string().email().nullable(),
  district: z.string(),
  school: z.string(),
  title: z.string().nullable(),
  roles: z.array(z.string()).nullable(),
  teacher_number: z.string().nullable(),
  state_id: z.string().nullable(),
}).openapi('CleverTeacher')

export const CleverSchoolSchema = z.object({
  id: z.string(),
  name: z.string(),
  district: z.string(),
  school_number: z.string().nullable(),
  state_id: z.string().nullable(),
  nces_id: z.string().nullable(),
  low_grade: z.string().nullable(),
  high_grade: z.string().nullable(),
}).openapi('CleverSchool')

export const CleverSectionSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  course: z.string(),
  teacher: z.string(),
  school: z.string(),
  grade: z.string().nullable(),
  subject: z.string().nullable(),
  term: z.object({ id: z.string(), name: z.string() }).nullable(),
}).openapi('CleverSection')

export const OneRosterPayloadSchema = z.object({
  orgs: z.array(OneRosterOrgSchema).nullable(),
  academicSessions: z.array(OneRosterAcademicSessionSchema).nullable(),
  courses: z.array(z.record(z.unknown())).nullable(),
  classes: z.array(OneRosterClassSchema).nullable(),
  users: z.array(OneRosterUserSchema).nullable(),
  enrollments: z.array(OneRosterEnrollmentSchema).nullable(),
}).openapi('OneRosterPayload', { description: 'OneRoster v1.1 bulk payload' })

export const CleverPayloadSchema = z.object({
  data: z.object({
    students: z.array(CleverStudentSchema).nullable(),
    teachers: z.array(CleverTeacherSchema).nullable(),
    schools: z.array(CleverSchoolSchema).nullable(),
    sections: z.array(CleverSectionSchema).nullable(),
    enrollments: z.array(z.record(z.unknown())).nullable(),
  }),
}).openapi('CleverPayload', { description: 'Clever API data payload' })

export const RosterSyncRequestSchema = z.object({
  provider: z.enum(['oneroster', 'clever']),
  dryRun: z.boolean().default(false),
  payload: OneRosterPayloadSchema.or(CleverPayloadSchema),
}).openapi('RosterSyncRequest')

export const RosterSyncResponseSchema = z.object({
  jobId: z.string().uuid(),
  status: z.enum(['running', 'completed', 'failed']),
  summary: z.object({
    created: z.number().int(),
    updated: z.number().int(),
    deactivated: z.number().int(),
    errors: z.number().int(),
  }),
  errors: z.array(z.object({
    index: z.number().int(),
    entity: z.string(),
    reason: z.string(),
  })).nullable(),
}).openapi('RosterSyncResponse')

export type OneRosterAcademicSession = z.infer<typeof OneRosterAcademicSessionSchema>
export type OneRosterOrg = z.infer<typeof OneRosterOrgSchema>
export type OneRosterUser = z.infer<typeof OneRosterUserSchema>
export type OneRosterEnrollment = z.infer<typeof OneRosterEnrollmentSchema>
export type OneRosterClass = z.infer<typeof OneRosterClassSchema>
export type CleverStudent = z.infer<typeof CleverStudentSchema>
export type CleverTeacher = z.infer<typeof CleverTeacherSchema>
export type CleverSchool = z.infer<typeof CleverSchoolSchema>
export type CleverSection = z.infer<typeof CleverSectionSchema>
export type RosterSyncRequest = z.infer<typeof RosterSyncRequestSchema>
export type RosterSyncResponse = z.infer<typeof RosterSyncResponseSchema>
